import { FC, useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  useMediaQuery,
  Tooltip,
  MenuItem,
} from '@mui/material'
import { CustomModal } from 'components/molecules/Modal/Modal'
import { ImportPhysiciansResponse } from 'lib/services/api/referring-physicians/types'
import styles from './ImportResultsModal.module.scss'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { useTheme } from '@mui/material/styles'
import { usePatientPhysician } from 'lib/services/api/referring-physicians/patient-physician'
import { useImportPhysicians } from 'lib/services/api/referring-physicians/import-physicians'
import { enqueueSnackbar } from 'notistack'
import { formatUSPhoneNumber } from 'common/utils'

// Import SPECIALITY array for dropdown
const SPECIALITY = [
  'Addiction Medicine',
  'Addiction Psychiatry',
  'Adolescent Medicine',
  'Adult Medicine',
  'Aerospace Medicine',
  'Allergy/Immunology',
  'Anesthesiologist',
  'Anesthesiology',
  'Audiology',
  'Cardiac Electrophysiology',
  'Cardiac Surgery',
  'Cardiologist',
  'Cardiology',
  'Certified Registered Nurse Anesthetist',
  'Chiropractic',
  'Clinical Neuropsychology',
  'Clinical Pathology',
  'Clinical Psychology',
  'Clinical Social Worker',
  'Colorectal Surgery',
  'Critical Care',
  'Cytopathology',
  'Dental Hygienist',
  'Dentist',
  'Dermatology',
  'Diagnostic Radiology',
  'Diet/Nutrition',
  'Electrodiagnostic Medicine',
  'Emergency Medicine',
  'Endocrinology',
  'Family Practice',
  'Foot and Ankle Surgery',
  'Forensic Pathology',
  'Forensic Psychiatry',
  'Gastroenterology',
  'GI',
  'General Surgery',
  'Geriatric Medicine',
  'Geriatric Psychiatry',
  'Gynecological Oncology',
  'Hand Surgery',
  'Hematology',
  'Hematology/Oncology',
  'Home Health Aide',
  'Hospice and Palliative Care',
  'Hospitalist',
  'Infectious Disease',
  'Internal Medicine',
  'Interventional Cardiology',
  'Interventional Pain Management',
  'Interventional Radiology',
  'IR',
  'Massage Therapist',
  'Maxillofacial Surgery',
  'Medical Oncology',
  'Neonatal-Perinatal Medicine',
  'Nephrology',
  'Nephrology - Pediatric',
  'Neurology',
  'Neuropsychiatry',
  'Neuroradiology',
  'Neurosurgery',
  'Nuclear Medicine',
  'Nurse',
  'Nurse Practitioner',
  'Obstetrics/Gynecology',
  'Occupational Medicine',
  'Occupational Therapist',
  'Oncology',
  'Ophthalmology',
  'Optometry',
  'Oral and Maxillofacial Surgery',
  'Oral Surgery',
  'OMFS',
  'Orthopedic Spine Surgery',
  'Orthopedic Surgery',
  'Orthopedics',
  'Osteopathic Manipulative Medicine',
  'Otolaryngology',
  'Otolaryngology/Facial Plastic Surgery',
  'Pain Management',
  'Pathology',
  'Pediatric Allergy/Immunology',
  'Pediatric Anesthesiologist',
  'Pediatric Cardiologist',
  'Pediatric Critical Care',
  'Pediatric Dentistry',
  'Pediatric Emergency Medicine',
  'Pediatric Endocrinology',
  'Pediatric Gastroenterology',
  'Pediatric Infectious Disease',
  'Pediatric Medicine',
  'Pediatric Medicine - Developmental & Behavioral',
  'Pediatric Oncology',
  'Pediatric Orthopedic Surgery',
  'Pediatric Otolaryngology',
  'Pediatric Pulmonology',
  'Pediatric Radiology',
  'Pediatric Rheumatology',
  'Pediatric Surgery',
  'Pediatric Urology',
  'Peripheral Vascular Disease',
  'Physical Medicine and Rehabilitation',
  'Physical Therapist',
  'Physician Assistant',
  'Plastic and Reconstructive Surgery',
  'Plastics',
  'Podiatry',
  'Preventive Medicine',
  'Psychiatry',
  'Psychology',
  'Psychosomatic Medicine',
  'Pulmonology',
  'Radiation Oncology',
  'Radiology',
  'Rehabilitation Medicine',
  'Reproductive Endocrinology',
  'Rheumatology',
  'Sleep Medicine',
  'Social Work',
  'Speech Language Pathologist',
  'Sports Medicine',
  'Surgical Oncology',
  'Therapeutic Radiology',
  'Therapist',
  'Therapy',
  'Thoracic Surgery',
  'Trauma Surgery',
  'Urology',
  'Vascular Surgery',
].sort()

interface ImportResultsModalProps {
  open: boolean
  onClose: () => void
  results: ImportPhysiciansResponse
  onPhysicianAdded?: () => void
}

const statusConfig = {
  successful: {
    color: 'success.main',
    icon: <CheckCircleIcon color="success" fontSize="small" />,
    label: 'Success',
  },
  success: {
    color: 'success.main',
    icon: <CheckCircleIcon color="success" fontSize="small" />,
    label: 'Success',
  },
  missing_patient: {
    color: 'error.main',
    icon: <ErrorIcon color="error" fontSize="small" />,
    label: 'Missing Patient',
  },
  missing_physician: {
    color: 'warning.main',
    icon: <WarningIcon color="warning" fontSize="small" />,
    label: 'Missing Physician',
  },
  created: {
    color: 'success.main',
    icon: <CheckCircleIcon color="success" fontSize="small" />,
    label: 'Created',
  },
  updated: {
    color: 'success.main',
    icon: <CheckCircleIcon color="success" fontSize="small" />,
    label: 'Updated',
  },
  physician_exists: {
    color: 'warning.main',
    icon: <WarningIcon color="warning" fontSize="small" />,
    label: 'Physician Exists',
  },
  validation_error: {
    color: 'error.main',
    icon: <ErrorIcon color="error" fontSize="small" />,
    label: 'Validation Error',
  },
  error: {
    color: 'error.main',
    icon: <ErrorIcon color="error" fontSize="small" />,
    label: 'Error',
  },
}

const ImportResultsModal: FC<ImportResultsModalProps> = ({
  open,
  onClose,
  results,
  onPhysicianAdded,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isXSmall = useMediaQuery(theme.breakpoints.down(400))
  const [emailInputs, setEmailInputs] = useState<{ [key: string]: string }>({})
  const [editingRow, setEditingRow] = useState<number | null>(null)
  const [updatedResults, setUpdatedResults] =
    useState<ImportPhysiciansResponse>(results)
  const [processingPhysicians, setProcessingPhysicians] = useState<Set<string>>(
    new Set()
  )
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

  const patientPhysicianMutation = usePatientPhysician()
  const importPhysiciansMutation = useImportPhysicians()

  // Format detection
  const isWinomsFormat = results?.format_type === 'winoms'
  const isDefaultFormat = results?.format_type === 'default'

  const handleEditClick = (idx: number) => {
    const row = updatedResults.results[idx]
    const physicianKey = `${row.physicianFirstName}-${row.physicianLastName}`

    if (processingPhysicians.has(physicianKey)) {
      return
    }

    // Initialize form fields for editing
    if (isDefaultFormat) {
      // For default format, initialize all editable fields
      setEmailInputs((prev) => ({
        ...prev,
        [`${idx}-firstName`]: row.physicianFirstName || '',
        [`${idx}-lastName`]: row.physicianLastName || '',
        [`${idx}-email`]: row.email || '',
        [`${idx}-mobile`]: row.mobile
          ? (() => {
              // Format phone number for display in edit mode
              const phone = row.mobile.toString()
              // If in +1XXXXXXXXXX format, convert to (XXX) XXX-XXXX for display
              if (/^\+1[2-9]\d{9}$/.test(phone)) {
                const digits = phone.substring(2) // Remove +1 prefix
                return formatUSPhoneNumber(digits)
              }
              // If already in (XXX) XXX-XXXX format, return as is
              if (/^\(\d{3}\) \d{3}-\d{4}$/.test(phone)) {
                return phone
              }
              // Extract digits and format to (XXX) XXX-XXXX
              const digits = phone.replace(/\D/g, '')
              if (digits.length === 10 && /^[2-9]/.test(digits)) {
                return formatUSPhoneNumber(digits)
              }
              // Return original if we can't format it
              return phone
            })()
          : '',
        [`${idx}-speciality`]: row.speciality || '',
      }))

      // Validate existing values and set field errors immediately
      const firstName = row.physicianFirstName || ''
      const lastName = row.physicianLastName || ''
      const email = row.email || ''
      const mobile = row.mobile || ''

      // Use functional update to ensure we don't lose previous errors
      setFieldErrors((prev) => {
        const newErrors = { ...prev }

        // Check firstName - matching types file validation
        if (!firstName || !firstName.trim()) {
          newErrors[`${idx}-firstName`] = 'Physician first name cannot be empty'
        } else if (firstName.trim().length < 2) {
          newErrors[`${idx}-firstName`] =
            'First name must be at least 2 characters long'
        } else if (!/^\S*$/.test(firstName.trim())) {
          newErrors[`${idx}-firstName`] = 'First name cannot contain spaces'
        }

        // Check lastName - matching types file validation
        if (!lastName || !lastName.trim()) {
          newErrors[`${idx}-lastName`] = 'Physician last name cannot be empty'
        } else if (lastName.trim().length < 2) {
          newErrors[`${idx}-lastName`] =
            'Last name must be at least 2 characters long'
        } else if (!/^\S*$/.test(lastName.trim())) {
          newErrors[`${idx}-lastName`] = 'Last name cannot contain spaces'
        }

        // Check email - matching types file validation
        if (!email || !email.trim()) {
          newErrors[`${idx}-email`] = 'Email is required'
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(email.trim())) {
            newErrors[`${idx}-email`] = 'Invalid email address'
          }
        }

        // Check mobile - handle various Excel import formats and validate
        if (mobile && mobile.trim()) {
          const mobileValue = mobile.trim()

          // Extract all digits from the input
          const digits = mobileValue.replace(/\D/g, '')

          // Check if we can convert to valid +1XXXXXXXXXX format
          if (digits.length === 10 && /^[2-9]/.test(digits)) {
            // Valid 10 digits (e.g., 9617661815) - will be converted to +19617661815
          } else if (
            digits.length === 11 &&
            digits.startsWith('1') &&
            /^1[2-9]/.test(digits)
          ) {
            // Valid 11 digits starting with 1 (e.g., 19617661815) - will be converted to +19617661815
          } else if (
            digits.length === 12 &&
            digits.startsWith('11') &&
            /^11[2-9]/.test(digits)
          ) {
            // Valid 12 digits starting with 11 (e.g., 119617661815) - will be converted to +19617661815
          } else if (/^\+1[2-9]\d{9}$/.test(mobileValue)) {
            // Already in correct +1XXXXXXXXXX format
          } else {
            newErrors[`${idx}-mobile`] =
              'Mobile number must have valid U.S. area code (2-9) and be convertible to +1XXXXXXXXXX format'
          }
        }

        return newErrors
      })
    } else if (isWinomsFormat) {
      // For Winoms format, only initialize email
      setEmailInputs((prev) => ({
        ...prev,
        [idx]: '',
      }))
    }

    setEditingRow(idx)
  }

  const handleEmailChange = (key: string | number, value: string) => {
    // Auto-format phone numbers for mobile field
    let formattedValue = value
    if (key.toString().includes('mobile') && typeof value === 'string') {
      // Extract only digits and limit to 10 characters
      const digits = value.replace(/\D/g, '').slice(0, 10)
      // Format to (XXX) XXX-XXXX display format
      formattedValue = formatUSPhoneNumber(digits)
    }

    setEmailInputs((prev) => ({ ...prev, [key]: formattedValue }))

    // Real-time field validation matching NewReferringPhysician.types.ts
    if (key.toString().includes('email') || typeof key === 'number') {
      if (!formattedValue || !formattedValue.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'Email is required',
        }))
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formattedValue.trim())) {
          setFieldErrors((prev) => ({
            ...prev,
            [key]: 'Invalid email address',
          }))
        } else {
          setFieldErrors((prev) => ({ ...prev, [key]: '' }))
        }
      }
    }

    // Validate first name - matching types file validation
    if (key.toString().includes('firstName')) {
      if (!formattedValue || !formattedValue.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'Physician first name cannot be empty',
        }))
      } else if (formattedValue.trim().length < 2) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'First name must be at least 2 characters long',
        }))
      } else if (!/^\S*$/.test(formattedValue.trim())) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'First name cannot contain spaces',
        }))
      } else {
        setFieldErrors((prev) => ({ ...prev, [key]: '' }))
      }
    }

    // Validate last name - matching types file validation
    if (key.toString().includes('lastName')) {
      if (!formattedValue || !formattedValue.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'Physician last name cannot be empty',
        }))
      } else if (formattedValue.trim().length < 2) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'Last name must be at least 2 characters long',
        }))
      } else if (!/^\S*$/.test(formattedValue.trim())) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: 'Last name cannot contain spaces',
        }))
      } else {
        setFieldErrors((prev) => ({ ...prev, [key]: '' }))
      }
    }

    // Validate mobile number - must be exactly 10 digits with valid area code
    if (key.toString().includes('mobile')) {
      if (formattedValue && formattedValue.trim()) {
        // Extract digits from the formatted display value
        const digits = formattedValue.replace(/\D/g, '')

        // Must be exactly 10 digits
        if (digits.length !== 10) {
          setFieldErrors((prev) => ({
            ...prev,
            [key]: 'Mobile number must be exactly 10 digits',
          }))
        } else if (!/^[2-9]/.test(digits)) {
          // Area code must start with 2-9
          setFieldErrors((prev) => ({
            ...prev,
            [key]: 'Mobile number must use a valid U.S. area code (2-9)',
          }))
        } else {
          // Valid 10 digits with valid area code
          setFieldErrors((prev) => ({ ...prev, [key]: '' }))
        }
      } else {
        // Clear error if field is empty (since it's optional)
        setFieldErrors((prev) => ({ ...prev, [key]: '' }))
      }
    }
  }

  const handleSaveEmail = useCallback(
    async (idx: number) => {
      const row = updatedResults.results[idx]

      if (isWinomsFormat) {
        // Winoms format: only email is editable
        const email = emailInputs[idx]

        if (!email || !email.trim()) {
          return
        }

        // Email validation matching types file
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email.trim())) {
          return
        }

        const physicianKey = `${row.physicianFirstName}-${row.physicianLastName}`
        setProcessingPhysicians((prev) => new Set(prev).add(physicianKey))

        try {
          // Find all patients with the same missing physician
          const patientsWithSamePhysician = updatedResults.results.filter(
            (result, resultIdx) =>
              result.physicianFirstName === row.physicianFirstName &&
              result.physicianLastName === row.physicianLastName &&
              result.status === 'missing_physician'
          )

          // Process each patient for this physician
          const promises = patientsWithSamePhysician.map(async (patientRow) => {
            if (!patientRow.patientMRN) {
              throw new Error('Patient MRN is required')
            }

            const payload = {
              patient_mrn: patientRow.patientMRN,
              physician_first_name: patientRow.physicianFirstName,
              physician_last_name: patientRow.physicianLastName,
              physician_phone: patientRow.physicianPhoneNumber || '',
              physician_email: email.trim(),
              physician_salutation: 'Dr.',
            }

            return patientPhysicianMutation.mutateAsync(payload)
          })

          await Promise.all(promises)

          // Update the status for all patients with this physician
          setUpdatedResults((prev) => ({
            ...prev,
            results: prev.results.map((result) =>
              result.physicianFirstName === row.physicianFirstName &&
              result.physicianLastName === row.physicianLastName &&
              result.status === 'missing_physician'
                ? { ...result, status: 'successful' }
                : result
            ),
          }))

          enqueueSnackbar(
            `Successfully created physician and assigned ${patientsWithSamePhysician.length} patient(s)`,
            { variant: 'success' }
          )

          // Trigger refresh of physicians list
          onPhysicianAdded?.()

          setEditingRow(null)
        } catch (error) {
          console.error('Error creating physician:', error)
          enqueueSnackbar('Failed to create physician. Please try again.', {
            variant: 'error',
          })
        } finally {
          setProcessingPhysicians((prev) => {
            const newSet = new Set(prev)
            newSet.delete(physicianKey)
            return newSet
          })
        }
      } else {
        // Default format: all fields are editable
        const email = emailInputs[`${idx}-email`]
        const firstName =
          emailInputs[`${idx}-firstName`] || row.physicianFirstName
        const lastName = emailInputs[`${idx}-lastName`] || row.physicianLastName
        const mobile = emailInputs[`${idx}-mobile`] || row.mobile
        const speciality = emailInputs[`${idx}-speciality`] || row.speciality

        if (!email || !email.trim()) {
          return
        }

        // Email validation matching types file
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email.trim())) {
          return
        }

        if (!firstName || !firstName.trim()) {
          return
        }

        if (!lastName || !lastName.trim()) {
          return
        }

        // Check for spaces in names
        if (!/^\S*$/.test(firstName.trim())) {
          return
        }

        if (!/^\S*$/.test(lastName.trim())) {
          return
        }

        // For default format, use the import API to handle both create and update
        try {
          // Create a CSV string with the single row data
          const csvData = `firstName,lastName,speciality,email,mobile\n${firstName.trim()},${lastName.trim()},${
            speciality || ''
          },${email.trim()},${
            mobile
              ? (() => {
                  // Extract raw 10 digits from the formatted display value
                  const digits = mobile.replace(/\D/g, '')
                  if (digits.length === 10 && /^[2-9]/.test(digits)) {
                    // Send raw 10 digits to backend (it will format to +1XXXXXXXXXX)
                    return digits
                  }
                  // Return empty if invalid
                  return ''
                })()
              : ''
          }`

          // Create a File object from the CSV data
          const csvBlob = new Blob([csvData], { type: 'text/csv' })
          const csvFile = new File([csvBlob], 'physician_update.csv', {
            type: 'text/csv',
          })

          // Call the import API with the single row
          const response = await importPhysiciansMutation.mutateAsync({
            file: csvFile,
          })

          if (response) {
            // The import API returns the same format as bulk import
            // Find the updated row in the response
            const updatedRow = response.results.find(
              (result: any) =>
                result.physicianFirstName === firstName.trim() &&
                result.physicianLastName === lastName.trim()
            )

            if (updatedRow) {
              // Update the local results to reflect the changes
              setUpdatedResults((prev) => ({
                ...prev,
                results: prev.results.map((result, resultIdx) =>
                  resultIdx === idx
                    ? {
                        ...result,
                        physicianFirstName: firstName.trim(),
                        physicianLastName: lastName.trim(),
                        email: email.trim(),
                        speciality: speciality || '',
                        mobile: mobile || '',
                        status: updatedRow.status,
                        message:
                          updatedRow.message ||
                          'Physician updated successfully',
                      }
                    : result
                ),
              }))

              const successMessage =
                updatedRow.status === 'success'
                  ? 'Physician created successfully'
                  : 'Physician updated successfully'
              enqueueSnackbar(successMessage, { variant: 'success' })
            } else {
              throw new Error('Failed to process physician update')
            }
          } else {
            throw new Error('Failed to import physician')
          }

          // Trigger refresh of physicians list
          onPhysicianAdded?.()
          setEditingRow(null)
        } catch (error) {
          console.error('Error updating physician:', error)
          enqueueSnackbar('Failed to update physician. Please try again.', {
            variant: 'error',
          })
        }
      }
    },
    [
      emailInputs,
      updatedResults,
      patientPhysicianMutation,
      onPhysicianAdded,
      isWinomsFormat,
      fieldErrors,
    ]
  )

  // Update results when props change
  useEffect(() => {
    setUpdatedResults(results)
  }, [results])

  const handleCloseImportResultsModal = () => {
    onClose()
    setUpdatedResults(results)
    setEditingRow(null)
    setFieldErrors({})
    setEmailInputs({})
  }

  return (
    <CustomModal
      open={open}
      header={'Import Results'}
      closeButtonOnly={true}
      secondaryButtonText="Done"
      secondaryButtonAction={handleCloseImportResultsModal}
      fitContentWidth={!isDefaultFormat}
      isLoadingState={false}
      className={`${styles.importResultsModal} ${
        isDefaultFormat ? styles.wideFormat : ''
      }`}
    >
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          maxHeight: '100%',
          overflow: 'auto',
          tableWidth: '100%',
          minHeight: '150px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'grey.100',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.400',
            borderRadius: '4px',
            '&:hover': {
              bgcolor: 'grey.500',
            },
          },
          '&::-webkit-scrollbar-corner': {
            bgcolor: 'grey.100',
          },
        }}
      >
        <Table
          stickyHeader
          size={isMobile ? 'small' : 'medium'}
          sx={{
            minWidth: isWinomsFormat
              ? isXSmall
                ? 350
                : isMobile
                ? 400
                : 500 // Winoms: 3 columns
              : isXSmall
              ? 700
              : isMobile
              ? 900
              : 1100, // Default: 6 columns (wider)
            '& .MuiTableCell-root': {
              fontSize: isXSmall ? '0.7rem' : isMobile ? '0.75rem' : '0.875rem',
              whiteSpace: isWinomsFormat ? 'nowrap' : 'normal', // Only nowrap for Winoms format
            },
          }}
        >
          <TableHead>
            <TableRow>
              {isWinomsFormat ? (
                <>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    Patient
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    Physician
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    First Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    Last Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    Speciality
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: 1,
                    }}
                  >
                    Mobile
                  </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  fontWeight: 600,
                  bgcolor: 'grey.50',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                  py: 1,
                }}
              >
                Status
              </TableCell>
              {!isWinomsFormat && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'grey.50',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                    py: 1,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {updatedResults?.results?.map((row, idx) => {
              const status = statusConfig[
                row.status as keyof typeof statusConfig
              ] || {
                color: 'text.secondary',
                icon: null,
                label: row.status,
              }

              return (
                <TableRow key={idx} hover>
                  {isWinomsFormat ? (
                    <>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.25,
                              fontSize: isXSmall
                                ? '0.7rem'
                                : isMobile
                                ? '0.75rem'
                                : '0.875rem',
                              lineHeight: 1.2,
                            }}
                          >
                            {row.patientFirstName || row.patientLastName
                              ? `${row.patientFirstName || ''} ${
                                  row.patientLastName || ''
                                }`.trim()
                              : '-'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: isXSmall
                                ? '0.6rem'
                                : isMobile
                                ? '0.65rem'
                                : '0.75rem',
                              lineHeight: 1.1,
                            }}
                          >
                            MRN: {row.patientMRN}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 0.25,
                              fontSize: isXSmall
                                ? '0.7rem'
                                : isMobile
                                ? '0.75rem'
                                : '0.875rem',
                              lineHeight: 1.2,
                            }}
                          >
                            {row.physicianFirstName || row.physicianLastName
                              ? `${row.physicianFirstName || ''} ${
                                  row.physicianLastName || ''
                                }`.trim()
                              : '-'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: isXSmall
                                ? '0.6rem'
                                : isMobile
                                ? '0.65rem'
                                : '0.75rem',
                              lineHeight: 1.1,
                            }}
                          >
                            {row.physicianPhoneNumber || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          {editingRow === idx && isDefaultFormat ? (
                            <TextField
                              size="small"
                              placeholder="First Name"
                              value={
                                emailInputs.hasOwnProperty(`${idx}-firstName`)
                                  ? emailInputs[`${idx}-firstName`]
                                  : row.physicianFirstName || ''
                              }
                              onChange={(e) =>
                                handleEmailChange(
                                  `${idx}-firstName`,
                                  e.target.value
                                )
                              }
                              disabled={patientPhysicianMutation.isLoading}
                              error={!!fieldErrors[`${idx}-firstName`]}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: isXSmall
                                    ? '0.65rem'
                                    : isMobile
                                    ? '0.7rem'
                                    : '0.8rem',
                                  py: isXSmall ? 0.25 : 0.5,
                                  px: isXSmall ? 0.5 : 0.75,
                                },
                                '& .MuiOutlinedInput-root': {
                                  borderColor: fieldErrors[`${idx}-firstName`]
                                    ? 'error.main'
                                    : 'inherit',
                                  '&.Mui-focused': {
                                    borderColor: fieldErrors[`${idx}-firstName`]
                                      ? 'error.main'
                                      : 'primary.main',
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                mb: 0.25,
                                fontSize: isXSmall
                                  ? '0.7rem'
                                  : isMobile
                                  ? '0.75rem'
                                  : '0.875rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.physicianFirstName || '-'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          {editingRow === idx && isDefaultFormat ? (
                            <TextField
                              size="small"
                              placeholder="Last Name"
                              value={
                                emailInputs.hasOwnProperty(`${idx}-lastName`)
                                  ? emailInputs[`${idx}-lastName`]
                                  : row.physicianLastName || ''
                              }
                              onChange={(e) =>
                                handleEmailChange(
                                  `${idx}-lastName`,
                                  e.target.value
                                )
                              }
                              disabled={patientPhysicianMutation.isLoading}
                              error={!!fieldErrors[`${idx}-lastName`]}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: isXSmall
                                    ? '0.65rem'
                                    : isMobile
                                    ? '0.7rem'
                                    : '0.8rem',
                                  py: isXSmall ? 0.25 : 0.5,
                                  px: isXSmall ? 0.5 : 0.75,
                                },
                                '& .MuiOutlinedInput-root': {
                                  borderColor: fieldErrors[`${idx}-lastName`]
                                    ? 'error.main'
                                    : 'inherit',
                                  '&.Mui-focused': {
                                    borderColor: fieldErrors[`${idx}-lastName`]
                                      ? 'error.main'
                                      : 'primary.main',
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                mb: 0.25,
                                fontSize: isXSmall
                                  ? '0.7rem'
                                  : isMobile
                                  ? '0.75rem'
                                  : '0.875rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.physicianLastName || '-'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          {editingRow === idx && isDefaultFormat ? (
                            <TextField
                              select
                              size="small"
                              placeholder="Select speciality"
                              value={
                                emailInputs.hasOwnProperty(`${idx}-speciality`)
                                  ? emailInputs[`${idx}-speciality`]
                                  : row.speciality || ''
                              }
                              onChange={(e) =>
                                handleEmailChange(
                                  `${idx}-speciality`,
                                  e.target.value
                                )
                              }
                              disabled={patientPhysicianMutation.isLoading}
                              sx={{
                                minWidth: isXSmall ? 120 : isMobile ? 130 : 170,
                                '& .MuiInputBase-input': {
                                  fontSize: isXSmall
                                    ? '0.65rem'
                                    : isMobile
                                    ? '0.7rem'
                                    : '0.8rem',
                                  py: isXSmall ? 0.25 : 0.5,
                                  px: isXSmall ? 0.5 : 0.75,
                                },
                                '& .MuiSelect-select': {
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  height: isXSmall ? 28 : 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                },
                                '& .MuiOutlinedInput-root': {
                                  height: isXSmall ? 28 : 32,
                                },
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                MenuProps: {
                                  PaperProps: {
                                    sx: {
                                      maxHeight: 200,
                                      width: isXSmall
                                        ? 140
                                        : isMobile
                                        ? 160
                                        : 200,
                                      '& .MuiMenuItem-root': {
                                        fontSize: isXSmall
                                          ? '0.65rem'
                                          : isMobile
                                          ? '0.7rem'
                                          : '0.8rem',
                                        py: isXSmall ? 0.5 : 0.75,
                                        px: isXSmall ? 0.75 : 1,
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem
                                value=""
                                disabled
                                sx={{ color: 'grey.500' }}
                              >
                                Select speciality
                              </MenuItem>
                              {SPECIALITY.map((speciality) => (
                                <MenuItem key={speciality} value={speciality}>
                                  {speciality}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                mb: 0.25,
                                fontSize: isXSmall
                                  ? '0.7rem'
                                  : isMobile
                                  ? '0.75rem'
                                  : '0.875rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.speciality || '-'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          {editingRow === idx && isDefaultFormat ? (
                            <TextField
                              size="small"
                              placeholder="Email"
                              value={
                                emailInputs.hasOwnProperty(`${idx}-email`)
                                  ? emailInputs[`${idx}-email`]
                                  : row.email || ''
                              }
                              onChange={(e) =>
                                handleEmailChange(
                                  `${idx}-email`,
                                  e.target.value
                                )
                              }
                              disabled={patientPhysicianMutation.isLoading}
                              error={!!fieldErrors[`${idx}-email`]}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: isXSmall
                                    ? '0.65rem'
                                    : isMobile
                                    ? '0.7rem'
                                    : '0.8rem',
                                  py: isXSmall ? 0.25 : 0.5,
                                  px: isXSmall ? 0.5 : 0.75,
                                },
                                '& .MuiOutlinedInput-root': {
                                  borderColor: fieldErrors[`${idx}-email`]
                                    ? 'error.main'
                                    : 'inherit',
                                  '&.Mui-focused': {
                                    borderColor: fieldErrors[`${idx}-email`]
                                      ? 'error.main'
                                      : 'primary.main',
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                mb: 0.25,
                                fontSize: isXSmall
                                  ? '0.7rem'
                                  : isMobile
                                  ? '0.75rem'
                                  : '0.875rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.email || '-'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                          py: isMobile ? 1 : 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: isXSmall ? 100 : isMobile ? 120 : 160,
                        }}
                      >
                        <Box>
                          {editingRow === idx && isDefaultFormat ? (
                            <TextField
                              size="small"
                              placeholder="Mobile"
                              value={
                                emailInputs.hasOwnProperty(`${idx}-mobile`)
                                  ? emailInputs[`${idx}-mobile`]
                                  : row.mobile || ''
                              }
                              onChange={(e) =>
                                handleEmailChange(
                                  `${idx}-mobile`,
                                  e.target.value
                                )
                              }
                              disabled={patientPhysicianMutation.isLoading}
                              error={!!fieldErrors[`${idx}-mobile`]}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: isXSmall
                                    ? '0.65rem'
                                    : isMobile
                                    ? '0.7rem'
                                    : '0.8rem',
                                  py: isXSmall ? 0.25 : 0.5,
                                  px: isXSmall ? 0.5 : 0.75,
                                },
                                '& .MuiOutlinedInput-root': {
                                  borderColor: fieldErrors[`${idx}-mobile`]
                                    ? 'error.main'
                                    : 'inherit',
                                  '&.Mui-focused': {
                                    borderColor: fieldErrors[`${idx}-mobile`]
                                      ? 'error.main'
                                      : 'primary.main',
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                mb: 0.25,
                                fontSize: isXSmall
                                  ? '0.7rem'
                                  : isMobile
                                  ? '0.75rem'
                                  : '0.875rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.mobile
                                ? (() => {
                                    const phone = row.mobile.toString()
                                    // If in +1XXXXXXXXXX format, convert to (XXX) XXX-XXXX for display
                                    if (/^\+1[2-9]\d{9}$/.test(phone)) {
                                      const digits = phone.substring(2) // Remove +1 prefix
                                      return formatUSPhoneNumber(digits)
                                    }
                                    // If already in (XXX) XXX-XXXX format, return as is
                                    if (/^\(\d{3}\) \d{3}-\d{4}$/.test(phone)) {
                                      return phone
                                    }
                                    // Extract digits and format to (XXX) XXX-XXXX for display
                                    const digits = phone.replace(/\D/g, '')
                                    if (
                                      digits.length === 10 &&
                                      /^[2-9]/.test(digits)
                                    ) {
                                      return formatUSPhoneNumber(digits)
                                    }
                                    // Return original if we can't format it
                                    return phone
                                  })()
                                : '-'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </>
                  )}

                  <TableCell
                    sx={{
                      verticalAlign: 'top',
                      px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                      py: isMobile ? 1 : 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      minWidth: isXSmall ? 90 : isMobile ? 110 : 130,
                    }}
                  >
                    <Tooltip
                      title={
                        row.message && row.status !== 'missing_physician'
                          ? row.message
                          : ''
                      }
                      placement="top-start"
                      arrow
                      disableHoverListener={
                        !row.message || row.status === 'missing_physician'
                      }
                    >
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: isXSmall ? 'wrap' : 'nowrap',
                          }}
                        >
                          {status.icon}
                          <Typography
                            variant="body2"
                            sx={{
                              color: status.color,
                              fontWeight: 600,
                              fontSize: isXSmall
                                ? '0.65rem'
                                : isMobile
                                ? '0.7rem'
                                : '0.8rem',
                              lineHeight: 1.1,
                            }}
                          >
                            {status.label}
                          </Typography>
                        </Box>

                        {/* Winoms format email editing - exactly as in original code */}
                        {isWinomsFormat &&
                          row.status === 'missing_physician' && (
                            <Box>
                              {editingRow === idx ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                  }}
                                >
                                  <TextField
                                    size="small"
                                    placeholder={
                                      isXSmall ? 'Email' : 'Enter email'
                                    }
                                    value={emailInputs[idx] || ''}
                                    onChange={(e) =>
                                      handleEmailChange(idx, e.target.value)
                                    }
                                    disabled={
                                      patientPhysicianMutation.isLoading
                                    }
                                    error={!!fieldErrors[idx]}
                                    sx={{
                                      '& .MuiInputBase-input': {
                                        fontSize: isXSmall
                                          ? '0.65rem'
                                          : isMobile
                                          ? '0.7rem'
                                          : '0.8rem',
                                        py: isXSmall ? 0.25 : 0.5,
                                        px: isXSmall ? 0.5 : 0.75,
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        minHeight: isXSmall ? 28 : 32,
                                      },
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 0.25,
                                    }}
                                  >
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleSaveEmail(idx)}
                                      disabled={
                                        !emailInputs[idx] ||
                                        !!fieldErrors[idx] ||
                                        patientPhysicianMutation.isLoading
                                      }
                                      sx={{
                                        fontSize: isXSmall
                                          ? '0.6rem'
                                          : isMobile
                                          ? '0.65rem'
                                          : '0.75rem',
                                        px: isXSmall ? 0.5 : 0.75,
                                        py: isXSmall ? 0.125 : 0.25,
                                        minWidth: isXSmall ? 30 : 40,
                                        minHeight: isXSmall ? 24 : 28,
                                      }}
                                    >
                                      {patientPhysicianMutation.isLoading
                                        ? processingPhysicians.has(
                                            `${row.physicianFirstName}-${row.physicianLastName}`
                                          )
                                          ? 'Processing...'
                                          : 'Saving...'
                                        : 'Save'}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => setEditingRow(null)}
                                      disabled={
                                        patientPhysicianMutation.isLoading
                                      }
                                      sx={{
                                        fontSize: isXSmall
                                          ? '0.6rem'
                                          : isMobile
                                          ? '0.65rem'
                                          : '0.75rem',
                                        px: isXSmall ? 0.5 : 0.75,
                                        py: isXSmall ? 0.125 : 0.25,
                                        minWidth: isXSmall ? 30 : 40,
                                        minHeight: isXSmall ? 24 : 28,
                                      }}
                                    >
                                      {isXSmall ? '✕' : 'Cancel'}
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleEditClick(idx)}
                                  disabled={
                                    patientPhysicianMutation.isLoading ||
                                    processingPhysicians.has(
                                      `${row.physicianFirstName}-${row.physicianLastName}`
                                    )
                                  }
                                  sx={{
                                    fontSize: isXSmall
                                      ? '0.6rem'
                                      : isMobile
                                      ? '0.65rem'
                                      : '0.75rem',
                                    textTransform: 'none',
                                    px: isXSmall ? 0.5 : 0.75,
                                    py: isXSmall ? 0.125 : 0.25,
                                    minHeight: isXSmall ? 24 : 28,
                                  }}
                                >
                                  {processingPhysicians.has(
                                    `${row.physicianFirstName}-${row.physicianLastName}`
                                  )
                                    ? 'Processing...'
                                    : isXSmall
                                    ? '+ Add'
                                    : '+ Add'}
                                </Button>
                              )}
                            </Box>
                          )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  {!isWinomsFormat && (
                    <TableCell
                      sx={{
                        verticalAlign: 'top',
                        px: isXSmall ? 0.5 : isMobile ? 1 : 2,
                        py: isMobile ? 1 : 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        minWidth: isXSmall ? 80 : isMobile ? 100 : 120,
                      }}
                    >
                      <Box>
                        {/* Edit button for default format - show for validation errors and other editable states */}
                        {isDefaultFormat &&
                          (row.status === 'validation_error' ||
                            row.status === 'updated' ||
                            row.status === 'error') &&
                          editingRow !== idx && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleEditClick(idx)}
                              disabled={patientPhysicianMutation.isLoading}
                              sx={{
                                fontSize: isXSmall
                                  ? '0.6rem'
                                  : isMobile
                                  ? '0.65rem'
                                  : '0.75rem',
                                textTransform: 'none',
                                borderRadius: 1,
                                borderColor: 'divider',
                                minHeight: isXSmall ? 20 : 24,
                                py: isXSmall ? 0.25 : 0.5,
                                px: isXSmall ? 0.5 : 0.75,
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  bgcolor: 'primary.light',
                                },
                              }}
                            >
                              Edit
                            </Button>
                          )}

                        {/* Save/Cancel buttons when editing */}
                        {editingRow === idx && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              gap: 0.5,
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSaveEmail(idx)}
                              disabled={
                                (isDefaultFormat
                                  ? !emailInputs[`${idx}-firstName`] ||
                                    !emailInputs[`${idx}-lastName`] ||
                                    !emailInputs[`${idx}-email`] ||
                                    !!fieldErrors[`${idx}-firstName`] ||
                                    !!fieldErrors[`${idx}-lastName`] ||
                                    !!fieldErrors[`${idx}-email`] ||
                                    !!fieldErrors[`${idx}-mobile`]
                                  : !emailInputs[idx] || !!fieldErrors[idx]) ||
                                patientPhysicianMutation.isLoading ||
                                importPhysiciansMutation.isLoading
                              }
                              sx={{
                                fontSize: isXSmall
                                  ? '0.6rem'
                                  : isMobile
                                  ? '0.65rem'
                                  : '0.75rem',
                                py: isXSmall ? 0.25 : 0.5,
                                px: isXSmall ? 0.5 : 0.75,
                                minHeight: isXSmall ? 20 : 24,
                              }}
                            >
                              {patientPhysicianMutation.isLoading ||
                              importPhysiciansMutation.isLoading
                                ? 'Saving...'
                                : 'Save'}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setEditingRow(null)}
                              disabled={patientPhysicianMutation.isLoading}
                              sx={{
                                fontSize: isXSmall
                                  ? '0.6rem'
                                  : isMobile
                                  ? '0.65rem'
                                  : '0.75rem',
                                py: isXSmall ? 0.25 : 0.5,
                                px: isXSmall ? 0.5 : 0.75,
                                minHeight: isXSmall ? 20 : 24,
                              }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </CustomModal>
  )
}

export default ImportResultsModal
